// FILE: pullRequest.ts
// Purpose: Schemas for the Pull Requests section — listing, detail, timeline and the write
//          actions (merge/close/reopen/ready, comment) a reviewer takes from it.
// Layer: Shared contracts (schema-only, no runtime logic).
// Exports: PullRequestState/ListEntry/Detail/Activity plus every WS input and result shape.
//
// GitHub-only on purpose. Upstream models four hosts behind a provider abstraction; this fork
// reads GitHub through the per-project encrypted token (`resolveProjectGitHubAuth`) over REST,
// because production has no `gh` binary to shell out to. Anything that only exists to tell hosts
// apart — provider kind, host identity, capability matrices — would be a constant here, so it is
// left out rather than carried as dead weight.
//
// Three shapes rather than one, because they cost different amounts and load independently:
//   - `PullRequestListEntry`  — one search page per project, what the list draws.
//   - `PullRequestDetail`     — one PR plus its checks, only when a row is opened.
//   - `PullRequestActivity`   — the conversation and commits, which page far deeper than the
//                               detail and must not hold the summary panel off screen.
//
// A pull request is addressed by `(projectId, number)` and never by repository name: the
// repository is resolved server-side from the project's stored remote, so a client cannot point
// a read — or a merge — at a repository the project does not own.

import { Schema } from "effect";

import {
  IsoDateTime,
  NonNegativeInt,
  PositiveInt,
  ProjectId,
  TrimmedNonEmptyString,
} from "./baseSchemas";

// ── Domain vocabulary ────────────────────────────────────────────────

/** The three states a pull request can actually be in. GitHub reports open|closed over REST and
 * merged is derived from `merged_at`, exactly as `GitHubPullRequestSummary` already does. */
export const PullRequestState = Schema.Literals(["open", "closed", "merged"]);
export type PullRequestState = typeof PullRequestState.Type;

/**
 * What a listing asks for. Separate from `PullRequestState` because a pull request is never
 * "all" — only a request for one can be.
 */
export const PullRequestListState = Schema.Literals(["all", "open", "closed", "merged"]);
export type PullRequestListState = typeof PullRequestListState.Type;

/** Whether the branch merges cleanly. "unknown" is GitHub still computing it, which it does
 * asynchronously — reading that as "conflicting" would put a red glyph on a healthy PR. */
export const PullRequestMergeability = Schema.Literals(["mergeable", "conflicting", "unknown"]);
export type PullRequestMergeability = typeof PullRequestMergeability.Type;

/** The one-glyph summary of a pull request's checks. Only ever set on the detail: a rollup per
 * row would be one extra request per row, which is what makes upstream's list slow. */
export const PullRequestChecksState = Schema.Literals(["passing", "failing", "pending"]);
export type PullRequestChecksState = typeof PullRequestChecksState.Type;

export const PullRequestCheckStatus = Schema.Literals([
  "pending",
  "success",
  "failure",
  "skipped",
  "neutral",
  "cancelled",
]);
export type PullRequestCheckStatus = typeof PullRequestCheckStatus.Type;

export const PullRequestMergeMethod = Schema.Literals(["merge", "squash", "rebase"]);
export type PullRequestMergeMethod = typeof PullRequestMergeMethod.Type;

/**
 * The writes this section offers. Deliberately four: every one maps to a single GitHub call and
 * is reversible or harmless. Upstream's `update-branch` and auto-merge are left out — they need
 * repository settings this fork does not read, and a button that silently does nothing is worse
 * than no button.
 */
export const PullRequestAction = Schema.Literals(["merge", "close", "reopen", "ready"]);
export type PullRequestAction = typeof PullRequestAction.Type;

export const PullRequestActor = Schema.Struct({
  login: TrimmedNonEmptyString,
  /** Null where GitHub does not report one, which is what the initials fall back to. */
  avatarUrl: Schema.NullOr(Schema.String),
});
export type PullRequestActor = typeof PullRequestActor.Type;

export const PullRequestLabel = Schema.Struct({
  name: TrimmedNonEmptyString,
  /** Six hex digits, no leading `#`, as GitHub returns them. Null where it returned none. */
  color: Schema.NullOr(Schema.String),
});
export type PullRequestLabel = typeof PullRequestLabel.Type;

export const PullRequestCheck = Schema.Struct({
  name: TrimmedNonEmptyString,
  status: PullRequestCheckStatus,
  description: Schema.NullOr(Schema.String),
  url: Schema.NullOr(Schema.String),
});
export type PullRequestCheck = typeof PullRequestCheck.Type;

// ── Listing ──────────────────────────────────────────────────────────

/**
 * One row of the list.
 *
 * `projectId` and `projectTitle` travel with every row because the list spans every project the
 * reader may see: a row with no repository beside it is unreadable once two projects are in view.
 */
export const PullRequestListEntry = Schema.Struct({
  projectId: ProjectId,
  projectTitle: TrimmedNonEmptyString,
  /** `owner/name`, as resolved from the project's own remote. */
  repository: TrimmedNonEmptyString,
  number: PositiveInt,
  title: TrimmedNonEmptyString,
  url: TrimmedNonEmptyString,
  /** Null for a pull request whose author's account is gone, which GitHub reports as no user. */
  author: Schema.NullOr(PullRequestActor),
  headBranch: TrimmedNonEmptyString,
  baseBranch: TrimmedNonEmptyString,
  state: PullRequestState,
  isDraft: Schema.Boolean,
  mergeability: PullRequestMergeability,
  /**
   * Zero where the counts were not read. GitHub's list endpoint omits them — only the single-PR
   * endpoint carries them — so a row shows a stat only once the detail has been opened. Zero is
   * drawn as no stat rather than as an empty change.
   */
  additions: NonNegativeInt,
  deletions: NonNegativeInt,
  labels: Schema.Array(PullRequestLabel),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type PullRequestListEntry = typeof PullRequestListEntry.Type;

/**
 * Narrowings the client asks for beyond `state`. Absent fields filter nothing.
 *
 * Free-text search is not here: it would need GitHub's search API with its own rate budget, and
 * the client already holds the page it is showing — so text narrowing happens there, over the
 * rows that arrived.
 */
export const PullRequestListFilters = Schema.Struct({
  draft: Schema.optional(Schema.Literals(["only", "hide"])),
  /** One login, matched against the row's author. */
  author: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(200))),
});
export type PullRequestListFilters = typeof PullRequestListFilters.Type;

/** GitHub refuses a page larger than this, so it is the cap here too. */
export const PULL_REQUEST_LIST_MAX_PER_PAGE = 100;
export const PULL_REQUEST_LIST_DEFAULT_PER_PAGE = 30;

/**
 * REST page-based paging rather than opaque cursors: `GET /pulls` pages by number, every project
 * is asked for the same page, and "load more" is page + 1. A cursor per repository would be
 * upstream's shape for a GraphQL search this fork does not run.
 */
export const PullRequestListInput = Schema.Struct({
  state: PullRequestListState,
  /** One project. Absent reads every project the caller may see. */
  projectId: Schema.optional(ProjectId),
  filters: Schema.optional(PullRequestListFilters),
  /** 1-based, as GitHub's REST pagination counts. */
  page: PositiveInt.pipe(Schema.withDecodingDefault(() => 1)),
  perPage: PositiveInt.check(Schema.isLessThanOrEqualTo(PULL_REQUEST_LIST_MAX_PER_PAGE)).pipe(
    Schema.withDecodingDefault(() => PULL_REQUEST_LIST_DEFAULT_PER_PAGE),
  ),
});
export type PullRequestListInput = typeof PullRequestListInput.Type;

/**
 * Why one project could not be read, so the banner names the fix rather than the symptom.
 * `message` carries the sentence to show; `reason` is what the client keys an action off — only
 * `no-credential` and `credential-invalid` have somewhere to send the reader.
 */
export const PullRequestErrorReason = Schema.Literals([
  /** The project has no stored GitHub token. */
  "no-credential",
  /** A token is stored and GitHub rejected it (401). */
  "credential-invalid",
  /** The token is valid but has no access to this repository (403/404 on a private repo). */
  "forbidden",
  /** The project's remote is not a GitHub URL, so there is nothing to read. */
  "not-github",
  /** GitHub's rate limit or secondary limit. Retrying later is the whole remedy. */
  "rate-limited",
  /** Anything else GitHub answered with, or a transport failure. */
  "upstream",
]);
export type PullRequestErrorReason = typeof PullRequestErrorReason.Type;

/** One project whose repository could not be read. Healthy projects still return their rows —
 * a single missing token must not empty the list. */
export const PullRequestProjectError = Schema.Struct({
  projectId: ProjectId,
  projectTitle: TrimmedNonEmptyString,
  reason: PullRequestErrorReason,
  message: TrimmedNonEmptyString,
});
export type PullRequestProjectError = typeof PullRequestProjectError.Type;

export const PullRequestListResult = Schema.Struct({
  /**
   * By update, newest first, across every project this answer covers.
   */
  entries: Schema.Array(PullRequestListEntry),
  errors: Schema.Array(PullRequestProjectError),
  /**
   * Who GitHub says the reader is, per project — the login behind that project's stored token.
   * It is what separates "mine" from "everyone else's" in the list, and it is per project because
   * two projects can hold two different tokens. A project whose viewer could not be resolved is
   * absent rather than present-and-undefined: an open-keyed record cannot carry an optional value
   * through the JSON codec.
   */
  viewers: Schema.Record(ProjectId, TrimmedNonEmptyString),
  /** The page these rows came from, echoed so a late answer cannot be appended to the wrong page. */
  page: PositiveInt,
  /** At least one project returned a full page, so there is probably another one. */
  hasMore: Schema.Boolean,
});
export type PullRequestListResult = typeof PullRequestListResult.Type;

// ── One pull request ─────────────────────────────────────────────────

/**
 * How every per-pull-request read and write addresses its subject. No repository field: the
 * server resolves it from the project, so a client cannot aim an operation elsewhere.
 */
export const PullRequestRef = Schema.Struct({
  projectId: ProjectId,
  number: PositiveInt,
});
export type PullRequestRef = typeof PullRequestRef.Type;

export const PullRequestDetailInput = PullRequestRef;
export type PullRequestDetailInput = typeof PullRequestDetailInput.Type;

/**
 * A pull request opened for reading: the list row plus everything only `GET /pulls/{n}` and the
 * check-runs endpoint carry.
 */
export const PullRequestDetail = Schema.Struct({
  ...PullRequestListEntry.fields,
  /** Markdown, empty for a pull request opened with no description. */
  body: Schema.String,
  changedFiles: NonNegativeInt,
  /** The head commit these checks and this diff belong to. Also what caches the diff. */
  headSha: TrimmedNonEmptyString,
  mergedAt: Schema.NullOr(IsoDateTime),
  closedAt: Schema.NullOr(IsoDateTime),
  /** Whoever has been asked for a review, plus whoever already left one. */
  reviewers: Schema.Array(PullRequestActor),
  checks: Schema.Array(PullRequestCheck),
  /**
   * Absent where GitHub reported no check runs at all, which is different from "none passed" —
   * a repository with no CI must not draw a pending glyph forever.
   */
  checksState: Schema.optional(PullRequestChecksState),
  /** How many remarks GitHub itself counts, which is the number the timeline tab shows. */
  commentCount: NonNegativeInt,
  /** The login behind this project's token, so the page can tell the reader's own remarks apart. */
  viewer: Schema.optional(TrimmedNonEmptyString),
});
export type PullRequestDetail = typeof PullRequestDetail.Type;

// ── Timeline ─────────────────────────────────────────────────────────

/**
 * Where one entry of the conversation came from. Kept as GitHub's own three sources rather than
 * flattened: `"review"` carries a verdict, `"inline"` hangs off a line of the diff, and
 * `"conversation"` is the discussion thread. Collapsing them would make "changes requested"
 * indistinguishable from a passing remark. Mirrors `GitHubPullRequestCommentKind` on the server.
 */
export const PullRequestCommentKind = Schema.Literals(["conversation", "review", "inline"]);
export type PullRequestCommentKind = typeof PullRequestCommentKind.Type;

/** GitHub's review verdicts, as `/pulls/{n}/reviews` spells them. */
export const PullRequestReviewState = Schema.Literals([
  "approved",
  "changes_requested",
  "commented",
  "dismissed",
  "pending",
]);
export type PullRequestReviewState = typeof PullRequestReviewState.Type;

export const PullRequestComment = Schema.Struct({
  /** GitHub's numeric id as a string: the three endpoints number their entries independently, so
   * only the pair (kind, id) is unique — the client keys rows on both. */
  id: TrimmedNonEmptyString,
  kind: PullRequestCommentKind,
  author: Schema.NullOr(PullRequestActor),
  body: Schema.String,
  createdAt: IsoDateTime,
  url: Schema.NullOr(Schema.String),
  /** Only for `kind: "inline"` — the file and line the remark hangs on. */
  path: Schema.NullOr(Schema.String),
  line: Schema.NullOr(PositiveInt),
  /** Only for `kind: "review"`. */
  reviewState: Schema.NullOr(PullRequestReviewState),
});
export type PullRequestComment = typeof PullRequestComment.Type;

export const PullRequestCommit = Schema.Struct({
  oid: TrimmedNonEmptyString,
  messageHeadline: Schema.String,
  committedDate: IsoDateTime,
  author: Schema.NullOr(PullRequestActor),
});
export type PullRequestCommit = typeof PullRequestCommit.Type;

export const PullRequestActivityInput = PullRequestRef;
export type PullRequestActivityInput = typeof PullRequestActivityInput.Type;

/**
 * The slower, conversation-shaped half of a pull request, read independently from the detail so a
 * PR with hundreds of remarks cannot hold its title, checks and actions off screen.
 */
export const PullRequestActivity = Schema.Struct({
  /** Oldest first, merged across the three comment endpoints. */
  comments: Schema.Array(PullRequestComment),
  /** Oldest first. */
  commits: Schema.Array(PullRequestCommit),
  /**
   * The read stopped at a bound of its own before GitHub ran out, so `comments` holds less than
   * the conversation does. The reader is told rather than shown a silently short list, which
   * reads as "no more feedback".
   */
  truncated: Schema.Boolean,
});
export type PullRequestActivity = typeof PullRequestActivity.Type;

// ── Writes ───────────────────────────────────────────────────────────

export const PullRequestRunActionInput = Schema.Struct({
  ...PullRequestRef.fields,
  action: PullRequestAction,
  /** Only read for `merge`; absent means GitHub's own default for the repository. */
  mergeMethod: Schema.optional(PullRequestMergeMethod),
});
export type PullRequestRunActionInput = typeof PullRequestRunActionInput.Type;

/**
 * What the pull request became. Returned instead of nothing so the row can settle immediately;
 * the client still invalidates its queries, but the button does not have to wait for a refetch to
 * stop looking pressed.
 */
export const PullRequestRunActionOutcome = Schema.Struct({
  action: PullRequestAction,
  state: PullRequestState,
  isDraft: Schema.Boolean,
});
export type PullRequestRunActionOutcome = typeof PullRequestRunActionOutcome.Type;

// Not trimmed: the body is markdown, where leading spaces open a code block and two trailing
// spaces are a line break. GitHub rejects bodies past 65536 characters, so that bound is enforced
// here to keep oversized payloads off the wire.
const PullRequestCommentBody = Schema.String.check(Schema.isNonEmpty()).check(
  Schema.isMaxLength(65_536),
);

export const PullRequestCommentInput = Schema.Struct({
  ...PullRequestRef.fields,
  body: PullRequestCommentBody,
});
export type PullRequestCommentInput = typeof PullRequestCommentInput.Type;

// ── Answers ──────────────────────────────────────────────────────────

/**
 * A pull request operation the outside world refused.
 *
 * Travels *inside* the answer rather than as a failure of the call, for a reason the transport
 * makes concrete: a websocket failure is serialized with `Cause.pretty`, which flattens a typed
 * error down to `"Error: <message>"` plus a server-side stack. Every field but the sentence is
 * lost on the way — including the `reason` that tells the reader whether to add a token, widen a
 * scope, or simply wait. Modelled as data, it arrives intact.
 *
 * This is for the six failures of the world outside: a missing or rejected credential, a scope
 * that does not reach, a remote that was never GitHub, a spent rate budget, and whatever else
 * GitHub answered. It is deliberately NOT for authorization: being refused `p.git_read` stays a
 * failure of the call, because a permission check that the client could ignore by forgetting to
 * read a field is not a permission check.
 */
export const PullRequestFailure = Schema.Struct({
  reason: PullRequestErrorReason,
  message: TrimmedNonEmptyString,
});
export type PullRequestFailure = typeof PullRequestFailure.Type;

const PullRequestFailedAnswer = Schema.Struct({
  kind: Schema.Literal("failed"),
  failure: PullRequestFailure,
});

export const PullRequestDetailResult = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("ok"), detail: PullRequestDetail }),
  PullRequestFailedAnswer,
]);
export type PullRequestDetailResult = typeof PullRequestDetailResult.Type;

export const PullRequestActivityResult = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("ok"), activity: PullRequestActivity }),
  PullRequestFailedAnswer,
]);
export type PullRequestActivityResult = typeof PullRequestActivityResult.Type;

export const PullRequestRunActionResult = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("ok"), outcome: PullRequestRunActionOutcome }),
  PullRequestFailedAnswer,
]);
export type PullRequestRunActionResult = typeof PullRequestRunActionResult.Type;

/** On success, the remark as GitHub stored it — so the timeline can show it without a second
 * read. */
export const PullRequestCommentResult = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("ok"), comment: PullRequestComment }),
  PullRequestFailedAnswer,
]);
export type PullRequestCommentResult = typeof PullRequestCommentResult.Type;

/**
 * Forget what the server has cached, so the next read asks GitHub. With a reference it forgets
 * that one pull request's detail, activity and diff; without one it forgets the listings.
 *
 * A request of its own rather than a flag on the reads: an explicit refresh somebody presses is
 * then the only thing that spends GitHub's rate budget, and every ordinary read shares a cache.
 */
export const PullRequestInvalidateInput = Schema.Struct({
  reference: Schema.optional(PullRequestRef),
});
export type PullRequestInvalidateInput = typeof PullRequestInvalidateInput.Type;

export const PullRequestInvalidateResult = Schema.Struct({
  /** The cache generation the next read will use. Echoed so a client can tell an invalidation
   * that took effect from one that raced with another. */
  epoch: NonNegativeInt,
});
export type PullRequestInvalidateResult = typeof PullRequestInvalidateResult.Type;
